/**
 * CameraUploadField.tsx
 *
 * Camera capture field component with custom API endpoint support.
 * - Displays UI for taking photos only (no file upload option)
 * - Validates that customApi is configured (required for form submission)
 * - Stores File objects temporarily in form state
 * - Uploads are handled during form submission via the FormRenderer
 */

import React, { useState, useEffect } from 'react';
import { Camera, X, AlertCircle } from 'lucide-react';
import { BaseFieldProps } from './shared/types';
import { FieldWrapper } from './shared';
import { Button } from '../ui/button';
import { toast } from 'react-toastify';

export const CameraUploadField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
  openCameraModal,
}) => {
  // Check if customApi is configured - required for image uploads
  const hasCustomApi = fieldConfig.customApi?.enabled && fieldConfig.customApi?.url;

  // Show error message if customApi is not configured and not in preview mode
  if (!hasCustomApi && mode !== 'preview') {
    const errorMessage = `Custom API endpoint not configured for image upload. Please configure the upload endpoint in the form builder.`;

    return (
      <FieldWrapper
        field={fieldConfig}
        errors={[...errors, errorMessage]}
        hasError={true}
      >
        <div className="border-2 border-red-300 rounded-md p-6 bg-red-50 flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700 font-medium">Image Upload Not Configured</p>
            <p className="text-xs text-red-600 mt-1">{errorMessage}</p>
          </div>
        </div>
      </FieldWrapper>
    );
  }

  // Show warning in preview mode if not configured
  if (!hasCustomApi && mode === 'preview') {
    return (
      <FieldWrapper
        field={fieldConfig}
        errors={errors}
        hasError={hasError}
        isPreview={true}
      >
        <div className="border-2 border-yellow-300 rounded-md p-6 bg-yellow-50 flex gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-700 font-medium">Image Upload Endpoint Not Configured</p>
            <p className="text-xs text-yellow-600 mt-1">
              Configure a custom API endpoint in the field settings before publishing
            </p>
          </div>
        </div>
      </FieldWrapper>
    );
  }

  const handleOpenCamera = () => {
    if (openCameraModal) {
      openCameraModal(fieldConfig);
    } else {
      toast.info('Camera modal not available in this mode');
    }
  };

  const handleRemove = () => {
    onChange(fieldConfig.id, null);
  };

  const isImage = value instanceof File;
  const isImageUrl = typeof value === 'string' && value.length > 0;

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setBlobUrl(url);
      setImageLoadError(false);
      setImageLoading(false);
      return () => URL.revokeObjectURL(url);
    }
    setBlobUrl(null);
    setImageLoadError(false); // Reset error state when value changes
    // If it's a URL string, mark as loading until image loads
    if (typeof value === 'string' && value.length > 0) {
      setImageLoading(true);
    }
  }, [value]);
  const imageUrl = isImage ? URL.createObjectURL(value) : isImageUrl ? value : null;
  const isPreviewMode = mode === 'preview';

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      <div className="space-y-3">
        {(isImage || isImageUrl) ? (
          // Show preview when image is selected or URL exists
          <div className="space-y-3">
            {isImageUrl && imageLoadError ? (
              // Previously submitted URL that failed to load
              <div className="border-2 border-yellow-300 rounded-lg p-6 bg-yellow-50 flex gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-700 font-medium">Previously submitted image exists, but unable to retrieve it</p>
                  <p className="text-xs text-yellow-600 mt-1">The image may have been deleted or is no longer accessible.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Display image from File object (blob URL) or uploaded URL string */}
                <img
                  src={isImageUrl ? value as string : blobUrl || undefined}
                  alt="preview"
                  className={`w-full max-w-md h-auto object-cover rounded-lg border border-gray-200 ${imageLoading && isImageUrl ? 'opacity-50' : ''}`}
                  onError={() => {
                    if (isImageUrl) {
                      setImageLoadError(true);
                      setImageLoading(false);
                    }
                  }}
                  onLoad={() => {
                    if (isImageUrl) {
                      setImageLoadError(false);
                      setImageLoading(false);
                    }
                  }}
                />
                {imageLoading && isImageUrl && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                )}
              </>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenCamera}
                disabled={disabled}
                className="gap-2"
              >
                <Camera className="h-4 w-4" />
                {isImage ? 'Retake Photo' : 'Take Photo'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Remove
              </Button>
            </div>
            {isImage && (
              <p className="text-xs text-gray-500">
                Photo will be uploaded when you submit the form
              </p>
            )}
          </div>
        ) : (
          // Show take photo button when no image selected
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenCamera}
              disabled={disabled}
              className="gap-2"
            >
              <Camera className="h-4 w-4" />
              Take Photo
            </Button>
          </div>
        )}
      </div>
    </FieldWrapper>
  );
});

CameraUploadField.displayName = 'CameraUploadField';
